export const DISPLAY_BASIS_INCL = 'incl';
export const DISPLAY_BASIS_EXCL = 'excl';

export const normalizeDisplayBasis = (basis) => {
  return basis === DISPLAY_BASIS_EXCL ? DISPLAY_BASIS_EXCL : DISPLAY_BASIS_INCL;
};

export const basisToPricesIncludeVat = (basis) => {
  return normalizeDisplayBasis(basis) === DISPLAY_BASIS_INCL;
};

export const pricesIncludeVatToBasis = (pricesIncludeVat) => {
  return pricesIncludeVat === false ? DISPLAY_BASIS_EXCL : DISPLAY_BASIS_INCL;
};

export const resolveDisplayBasis = (document = {}, organization = {}) => {
  // Support both snake_case and camelCase payloads.
  const displayBasis = document.display_basis ?? document.displayBasis;
  const pricesIncludeVat = document.prices_include_vat ?? document.pricesIncludeVat;
  if (displayBasis) {
    return normalizeDisplayBasis(displayBasis);
  }
  if (pricesIncludeVat != null) {
    return pricesIncludeVatToBasis(pricesIncludeVat);
  }

  const defaults = organization.proposal_defaults || document.proposal_defaults || {};
  const defaultDisplayBasis = defaults.display_basis ?? defaults.displayBasis;
  const defaultPricesIncludeVat = defaults.prices_include_vat ?? defaults.pricesIncludeVat;
  if (defaultDisplayBasis) {
    return normalizeDisplayBasis(defaultDisplayBasis);
  }
  if (defaultPricesIncludeVat != null) {
    return pricesIncludeVatToBasis(defaultPricesIncludeVat);
  }

  return DISPLAY_BASIS_INCL;
};

/**
 * Resolve the display basis for PROPOSAL PRESENTATION (line prices incl or
 * excl VAT on the quote). Differs from `resolveDisplayBasis` in the org
 * fallback: it reads `proposal_defaults.proposal_display_basis` — the
 * dedicated presentation setting — instead of the catalog storage-basis keys
 * (`display_basis` / `prices_include_vat`). Those describe how product prices
 * are entered/stored and are deliberately NOT used here: an org can keep an
 * excl-VAT catalog and still present B2C quotes incl-VAT.
 *
 * Document-level hints (an existing proposal's own basis) always win.
 * Hard fallback is 'incl' (B2C default).
 */
export const resolveProposalDisplayBasis = (document = {}, organization = {}) => {
  const displayBasis = document.display_basis ?? document.displayBasis;
  const pricesIncludeVat = document.prices_include_vat ?? document.pricesIncludeVat;
  if (displayBasis) {
    return normalizeDisplayBasis(displayBasis);
  }
  if (pricesIncludeVat != null) {
    return pricesIncludeVatToBasis(pricesIncludeVat);
  }

  const defaults = organization.proposal_defaults || document.proposal_defaults || {};
  const proposalBasis = defaults.proposal_display_basis ?? defaults.proposalDisplayBasis;
  if (proposalBasis) {
    return normalizeDisplayBasis(proposalBasis);
  }

  return DISPLAY_BASIS_INCL;
};

export const roundMoney = (amount, decimals = 4) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export const roundDisplayMoney = (amount) => roundMoney(amount, 2);

export const PRICE_ROUNDING_MODES = ['nearest', 'up', 'down'];

// Must match the settings UI default ("Afronden op € 1,00"). Missing increment
// used to disable rounding in the cart while the backend silently rounded to
// cents, so the dropdown could show €1 without that value ever being stored.
export const DEFAULT_PRICE_ROUNDING_INCREMENT = 1;

/**
 * Normalize a raw `price_rounding` config object (from
 * `organization.proposal_defaults.price_rounding`) into a safe, usable shape.
 * Returns `null` when rounding is disabled, so callers can cheaply branch on a
 * falsy value and keep the legacy (cents-only) behaviour.
 *
 * @param {Object} cfg
 * @returns {{ increment: number, mode: string, minNonzero: boolean } | null}
 */
export const normalizePriceRounding = (cfg) => {
  if (!cfg || typeof cfg !== 'object') return null;
  if (cfg.enabled !== true) return null;
  const parsed = Number(cfg.increment);
  const increment =
    Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PRICE_ROUNDING_INCREMENT;
  const mode = PRICE_ROUNDING_MODES.includes(cfg.mode) ? cfg.mode : 'nearest';
  return {
    increment,
    mode,
    minNonzero: cfg.min_nonzero === true || cfg.minNonzero === true,
  };
};

/**
 * Resolve a normalized price-rounding config from an organization or form
 * payload (looks in `proposal_defaults.price_rounding`, snake/camel tolerant).
 *
 * @param {Object} source - organization, form, or document object
 * @returns {{ increment: number, mode: string, minNonzero: boolean } | null}
 */
export const resolvePriceRounding = (source = {}) => {
  if (!source || typeof source !== 'object') return null;
  const defaults =
    source.proposal_defaults ||
    source.proposalDefaults ||
    source.organization?.proposal_defaults ||
    source.organization?.proposalDefaults ||
    {};
  return normalizePriceRounding(defaults.price_rounding ?? defaults.priceRounding);
};

/**
 * Round an amount to the nearest multiple of `increment` using the given mode.
 * Falls back to cent-rounding when the increment is not a positive number.
 *
 * @param {number} amount
 * @param {number} increment - e.g. 0.5, 1, 5, 10
 * @param {'nearest'|'up'|'down'} [mode='nearest']
 * @returns {number}
 */
export const roundToIncrement = (amount, increment, mode = 'nearest') => {
  const x = Number(amount);
  const step = Number(increment);
  if (!Number.isFinite(x)) return 0;
  if (!Number.isFinite(step) || step <= 0) return roundDisplayMoney(x);
  const q = x / step;
  let r;
  if (mode === 'up') r = Math.ceil(q);
  else if (mode === 'down') r = Math.floor(q);
  else r = Math.round(q);
  return roundDisplayMoney(r * step);
};

/**
 * Apply the configured price-rounding to a single incl-VAT amount. When
 * rounding is disabled the amount is returned unchanged (no cent-rounding) so
 * existing behaviour is preserved bit-for-bit.
 *
 * @param {number} amountInclVat
 * @param {Object|null} roundingConfig - raw or normalized config
 * @returns {number}
 */
export const roundInclVatAmount = (amountInclVat, roundingConfig) => {
  const x = Number(amountInclVat) || 0;
  const cfg =
    roundingConfig && roundingConfig.increment != null
      ? roundingConfig
      : normalizePriceRounding(roundingConfig);
  if (!cfg) return x;
  let rounded = roundToIncrement(x, cfg.increment, cfg.mode);
  if (cfg.minNonzero && x > 0 && rounded === 0) {
    rounded = roundDisplayMoney(cfg.increment);
  }
  return rounded;
};

/**
 * Round a single line item ("los afronden") on its incl-VAT unit price, keeping
 * the excl-VAT canonical value consistent. The incl-VAT price is the anchor:
 * it is rounded to the configured increment, then the excl price is derived
 * back from it so that totals (which sum incl prices when prices_include_vat)
 * land on exact multiples of the increment.
 *
 * Mutates and returns a shallow copy of the item.
 *
 * @param {Object} item - { unit_price, unit_price_excl_vat?, vat_rate }
 * @param {Object|null} roundingConfig
 * @param {'incl'|'excl'} [displayBasis='incl']
 * @returns {Object}
 */
export const roundLineItemIncl = (item = {}, roundingConfig = null, displayBasis = DISPLAY_BASIS_INCL) => {
  const cfg =
    roundingConfig && roundingConfig.increment != null
      ? roundingConfig
      : normalizePriceRounding(roundingConfig);
  if (!cfg) return item;

  const rate = Number(item.vat_rate) || 0;
  const factor = 1 + rate / 100;

  const exclUnit = deriveUnitPriceExclVat(item, displayBasis);
  const inclUnit = exclUnit * factor;

  let roundedIncl = roundToIncrement(inclUnit, cfg.increment, cfg.mode);
  if (cfg.minNonzero && inclUnit > 0 && roundedIncl === 0) {
    roundedIncl = roundDisplayMoney(cfg.increment);
  }

  const newExcl = factor !== 0 ? roundedIncl / factor : roundedIncl;
  const basisIncl = normalizeDisplayBasis(displayBasis) === DISPLAY_BASIS_INCL;

  return {
    ...item,
    unit_price_excl_vat: roundMoney(newExcl),
    unit_price: basisIncl ? roundedIncl : roundDisplayMoney(newExcl),
  };
};

/**
 * Apply per-line incl-VAT rounding to a list of items. No-op when rounding is
 * disabled. Use this at the point where configurator results are materialized
 * into proposal/order line items.
 *
 * @param {Object[]} items
 * @param {Object|null} roundingConfig
 * @param {'incl'|'excl'} [displayBasis='incl']
 * @returns {Object[]}
 */
export const applyLineRounding = (items = [], roundingConfig = null, displayBasis = DISPLAY_BASIS_INCL) => {
  const cfg =
    roundingConfig && roundingConfig.increment != null
      ? roundingConfig
      : normalizePriceRounding(roundingConfig);
  if (!cfg || !Array.isArray(items)) return items;
  return items.map((it) => roundLineItemIncl(it, cfg, displayBasis));
};

export const inclVatFromExcl = (amount, vatRate = 21) => {
  const rate = Number(vatRate) || 0;
  return roundMoney((Number(amount) || 0) * (1 + rate / 100));
};

export const exclVatFromIncl = (amount, vatRate = 21) => {
  const rate = Number(vatRate) || 0;
  return roundMoney((Number(amount) || 0) / (1 + rate / 100));
};

export const deriveUnitPriceExclVat = (item = {}, displayBasis = DISPLAY_BASIS_INCL) => {
  if (item.unit_price_excl_vat != null) {
    return roundMoney(item.unit_price_excl_vat);
  }

  const unitPrice = Number(item.unit_price) || 0;
  if (normalizeDisplayBasis(displayBasis) === DISPLAY_BASIS_EXCL) {
    return roundMoney(unitPrice);
  }
  return exclVatFromIncl(unitPrice, item.vat_rate);
};

export const displayUnitPrice = (item = {}, displayBasis = DISPLAY_BASIS_INCL) => {
  const excl = deriveUnitPriceExclVat(item, displayBasis);
  if (normalizeDisplayBasis(displayBasis) === DISPLAY_BASIS_EXCL) {
    return excl;
  }
  return inclVatFromExcl(excl, item.vat_rate);
};

export const toDisplayBasisAmount = (
  amount,
  vatRate = 21,
  fromBasis = DISPLAY_BASIS_EXCL,
  toBasis = DISPLAY_BASIS_INCL
) => {
  const normalizedFrom = normalizeDisplayBasis(fromBasis);
  const normalizedTo = normalizeDisplayBasis(toBasis);
  if (normalizedFrom === normalizedTo) return roundMoney(amount);
  if (normalizedTo === DISPLAY_BASIS_INCL) return inclVatFromExcl(amount, vatRate);
  return exclVatFromIncl(amount, vatRate);
};

export const prepareLineItemForSave = (item = {}, displayBasis = DISPLAY_BASIS_INCL) => {
  const unitPriceExclVat = deriveUnitPriceExclVat(
    { ...item, unit_price_excl_vat: undefined },
    displayBasis
  );
  return {
    ...item,
    unit_price: displayUnitPrice({ ...item, unit_price_excl_vat: unitPriceExclVat }, displayBasis),
    unit_price_excl_vat: unitPriceExclVat,
  };
};

export const calculateVatBreakdown = (items = [], displayBasis = DISPLAY_BASIS_INCL) => {
  const rows = new Map();

  items.forEach((item) => {
    const quantity = Number(item.quantity) || 0;
    const rate = Number(item.vat_rate) || 0;
    const net = roundMoney(quantity * deriveUnitPriceExclVat(item, displayBasis));
    const vat = roundMoney(net * (rate / 100));
    const current = rows.get(rate) || { rate, net: 0, vat: 0, gross: 0 };
    current.net = roundMoney(current.net + net);
    current.vat = roundMoney(current.vat + vat);
    current.gross = roundMoney(current.net + current.vat);
    rows.set(rate, current);
  });

  return Array.from(rows.values()).sort((a, b) => a.rate - b.rate);
};

export const calculateDocumentTotals = (items = [], displayBasis = DISPLAY_BASIS_INCL) => {
  const vatBreakdown = calculateVatBreakdown(items, displayBasis);
  const subtotalExclVat = roundMoney(vatBreakdown.reduce((sum, row) => sum + row.net, 0));
  const vatAmount = roundMoney(vatBreakdown.reduce((sum, row) => sum + row.vat, 0));

  return {
    subtotal_excl_vat: roundDisplayMoney(subtotalExclVat),
    vat_amount: roundDisplayMoney(vatAmount),
    total_amount: roundDisplayMoney(subtotalExclVat + vatAmount),
    vat_breakdown: vatBreakdown.map((row) => ({
      rate: row.rate,
      net: roundDisplayMoney(row.net),
      vat: roundDisplayMoney(row.vat),
      gross: roundDisplayMoney(row.gross),
    })),
  };
};
